import { useState } from "react";
import { Helmet } from "react-helmet-async";
import ReactHeader from "./reactHeader";
import FooterSection from "./footerSection";
import AnimatedBlock from "./animatedBlock";
import "./trustDownloadGuide.css";

const GUIDES = {
    chrome: {
        label: "Google Chrome",
        logo: "/Chrome_Logo.png",
        slides: [
            {
                image: "/Chrome_Slide1.png",
                alt: "Google Chrome downloads bar with the Open icon highlighted next to the Khor Developer installer",
                title: "Open the download",
                body: (
                    <>
                        In your browser's download bar, click the download icon, then select{" "}
                        <strong>Open</strong> next to KhorDeveloperSetup.exe.
                    </>
                ),
            },
            {
                image: "/Chrome_Slide2.png",
                alt: "Windows protected your PC dialog with the Run anyway button",
                title: "Allow the installer to run",
                body: (
                    <>
                        When the <strong>Windows protected your PC</strong> message appears, click{" "}
                        <strong>More info</strong>, then select <strong>Run anyway</strong>.
                    </>
                ),
            },
        ],
    },
    edge: {
        label: "Microsoft Edge",
        logo: "/Edge_Logo.png",
        slides: [
            {
                image: "/Edge_Slide1.png",
                alt: "Microsoft Edge downloads flyout with the More options menu open and Keep highlighted",
                title: "Keep the file",
                body: (
                    <>
                        Click the <strong>⋯ (More options)</strong> next to the download, then select{" "}
                        <strong>Keep</strong>.
                    </>
                ),
            },
            {
                image: "/Edge_Slide2.png",
                alt: "Microsoft Edge dialog confirming trust, with Keep anyway highlighted",
                title: "Confirm you trust the file",
                body: (
                    <>
                        Click the <strong>˅ arrow</strong> next to <strong>Delete</strong>, then select{" "}
                        <strong>Keep anyway</strong>.
                    </>
                ),
            },
        ],
    },
};

function detectBrowser() {
    if (typeof navigator === "undefined") return "chrome";
    const ua = navigator.userAgent || "";
    if (/Edg\//i.test(ua)) return "edge";
    return "chrome";
}

const TrustDownloadGuideSection = () => {
    const [browser, setBrowser] = useState(detectBrowser);
    const [slideIndex, setSlideIndex] = useState(0);

    const guide = GUIDES[browser];
    const slide = guide.slides[slideIndex];
    const slideCount = guide.slides.length;

    const selectBrowser = (key) => {
        setBrowser(key);
        setSlideIndex(0);
    };

    const goToSlide = (i) => setSlideIndex((i + slideCount) % slideCount);

    return (
        <section className="tdg-section">
            <div className="tdg-container">
                <AnimatedBlock>
                    <p className="is-eyebrow">Installation Help</p>
                    <h1 className="is-headline">Trusting the Khor Developer Download</h1>
                    <p className="is-subtext">
                        New downloads are sometimes flagged by Windows or your browser. If that happens,
                        select your browser below and follow the steps to continue safely.
                    </p>
                </AnimatedBlock>

                <AnimatedBlock>
                    <div className="tdg-browser-toggle" role="tablist" aria-label="Select your browser">
                        {Object.entries(GUIDES).map(([key, g]) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={browser === key}
                                className={`tdg-browser-pill ${browser === key ? "tdg-browser-pill--active" : ""}`}
                                onClick={() => selectBrowser(key)}
                            >
                                <img src={g.logo} alt="" className="tdg-browser-pill-logo" />
                                {g.label}
                            </button>
                        ))}
                    </div>
                </AnimatedBlock>

                <AnimatedBlock>
                    <div className="tdg-slideshow">
                        <div className="tdg-slide-image-wrap">
                            <img
                                key={slide.image}
                                src={slide.image}
                                alt={slide.alt}
                                className="tdg-slide-image"
                                loading="lazy"
                                decoding="async"
                            />
                            {slideCount > 1 && (
                                <>
                                    <button
                                        type="button"
                                        className="tdg-slide-nav tdg-slide-nav--prev"
                                        onClick={() => goToSlide(slideIndex - 1)}
                                        aria-label="Previous step"
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type="button"
                                        className="tdg-slide-nav tdg-slide-nav--next"
                                        onClick={() => goToSlide(slideIndex + 1)}
                                        aria-label="Next step"
                                    >
                                        ›
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="tdg-slide-text">
                            <span className="tdg-slide-count">
                                Step {slideIndex + 1} of {slideCount}
                            </span>
                            <h2 className="tdg-slide-heading">{slide.title}</h2>
                            <p className="tdg-slide-body">{slide.body}</p>

                            {slideCount > 1 && (
                                <div className="tdg-slide-dots">
                                    {guide.slides.map((_, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={`tdg-slide-dot ${i === slideIndex ? "tdg-slide-dot--active" : ""}`}
                                            onClick={() => goToSlide(i)}
                                            aria-label={`Go to step ${i + 1}`}
                                            aria-current={i === slideIndex}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </AnimatedBlock>

                <AnimatedBlock>
                    <p className="tdg-support-note">
                        Still stuck? <a href="/contact" className="installation-link">Contact our support team</a> and
                        we'll help you get set up.
                    </p>
                </AnimatedBlock>
            </div>
        </section>
    );
};

const TrustDownloadGuide = () => (
    <div className="App">
        <Helmet>
            <title>Trusting the Khor Developer Download | Khor Developer</title>
            <meta name="robots" content="noindex, follow" />
            <meta
                name="description"
                content="Step-by-step instructions for trusting and running the Khor Developer installer in Chrome and Edge."
            />
            <meta property="og:title" content="Trusting the Khor Developer Download | Khor Developer" />
            <meta property="og:url" content="https://www.khordeveloper.com/trusting-the-download" />
            <link rel="canonical" href="https://www.khordeveloper.com/trusting-the-download" />
        </Helmet>
        <ReactHeader />
        <TrustDownloadGuideSection />
        <FooterSection />
    </div>
);

export default TrustDownloadGuide;
